import { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

let dispatch = null;

/** Styled drop-in for window.confirm — resolves true/false. */
export function confirmDialog(message, opts = {}) {
  if (!dispatch) return Promise.resolve(window.confirm(message));
  return new Promise((resolve) => dispatch({ type: "confirm", message, resolve, ...opts }));
}

/** Styled drop-in for window.prompt — resolves the string, or null on cancel. */
export function promptDialog(message, opts = {}) {
  if (!dispatch) return Promise.resolve(window.prompt(message));
  return new Promise((resolve) => dispatch({ type: "prompt", message, resolve, ...opts }));
}

export function ConfirmHost() {
  const [req, setReq] = useState(null);
  const [value, setValue] = useState("");
  const settled = useRef(false);

  useEffect(() => {
    dispatch = (r) => { settled.current = false; setValue(""); setReq(r); };
    return () => { dispatch = null; };
  }, []);

  const settle = (result) => {
    if (!settled.current && req) { settled.current = true; req.resolve(result); }
    setReq(null);
  };

  if (!req) return null;
  const isPrompt = req.type === "prompt";

  return (
    <AlertDialog open onOpenChange={(o) => { if (!o) settle(isPrompt ? null : false); }}>
      <AlertDialogContent
        data-testid="confirm-dialog"
        className="max-w-sm border-zinc-800 bg-zinc-950/95 backdrop-blur-xl text-zinc-100 rounded-xl p-5 gap-4"
      >
        <AlertDialogHeader className="gap-2">
          <AlertDialogTitle className="mono text-[13px] tracking-wide text-zinc-100 lowercase">
            {req.title || (isPrompt ? "input needed" : "please confirm")}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[13px] leading-relaxed text-zinc-400 whitespace-pre-line">
            {req.message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {isPrompt && (
          <Input
            autoFocus
            data-testid="confirm-dialog-input"
            value={value}
            placeholder={req.placeholder || ""}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") settle(value); }}
            className="h-9 bg-zinc-900 border-zinc-700 text-zinc-100 text-[13px]"
          />
        )}
        <AlertDialogFooter className="gap-2 sm:gap-2">
          <Button
            data-testid="confirm-dialog-cancel"
            variant="outline"
            onClick={() => settle(isPrompt ? null : false)}
            className="mono text-[11px] h-8 px-4 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
          >
            {req.cancelText || "cancel"}
          </Button>
          <Button
            data-testid="confirm-dialog-confirm"
            onClick={() => settle(isPrompt ? value : true)}
            className={`mono text-[11px] h-8 px-4 ${
              req.destructive
                ? "bg-rose-500/15 text-rose-300 border border-rose-500/40 hover:bg-rose-500/25"
                : "bg-zinc-100 text-zinc-950 hover:bg-white"
            }`}
          >
            {req.confirmText || (isPrompt ? "submit" : "confirm")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
